import React, { useState } from "react";

import { BSBtn, BSCard } from "traec-react/utils/bootstrap";
import { ProjectPermission } from "../utils/permissions";

import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import DataTable from "react-data-table-component";
import Im from "immutable";
import { ActionIcon } from "./utils";
import { Hide } from "../../utils";
import Octicon from "react-octicon";
import { useProjectPermissions } from "storybook-dashboard/utils/hooks";

const updateAuthGroup = ({ projectId, authGroup, data, hideModal, trigger }) => {
  let authGroupId = authGroup?.get("uid");
  let url = `/api/project/${projectId}/authgroup/` + (authGroupId ? `${authGroupId}/` : "");
  let method = authGroupId ? "PATCH" : "POST";
  doFetch(url, method, data).then(() => {
    if (trigger) trigger();
    if (hideModal) hideModal();
  });
};

function NewAuthGroupModal(props) {
  let { hideModal } = useModal();
  let { authGroup } = props;
  return (
    <Modal title={authGroup ? "Edit Permission Group" : "Create New Permission Group"}>
      <div className="p-3">
        <p className="text-muted mb-3">
          {authGroup
            ? "Update the permission group settings below."
            : "Define a new permission group with specific access rights for different project functions."}
        </p>
        <AuthGroupForm {...props} hideModal={hideModal} />
      </div>
    </Modal>
  );
}

const getColumns = (props) => [
  {
    name: "Permission Group Name",
    sortable: true,
    selector: (item) => item.get("name"),
    grow: 3,
    wrap: true,
    cell: (item) => (
      <div className="d-flex flex-column">
        <span className="fw-medium">{item.get("name")}</span>
        {item.get("is_admin") && (
          <small className="text-warning">
            <Octicon name="shield" className="me-1" />
            Administrator Access
          </small>
        )}
      </div>
    ),
  },
  {
    name: "Access Level",
    sortable: true,
    selector: (item) => (item.get("is_admin") ? "Administrator" : "Standard"),
    grow: 1,
    wrap: true,
    cell: (item) => {
      const isAdmin = item.get("is_admin");
      return isAdmin ? (
        <span className="badge bg-danger">Administrator</span>
      ) : (
        <span className="badge bg-success">Standard</span>
      );
    },
  },
  {
    name: "Permissions",
    selector: (item) => item.getIn(["policy_json", "actions"], Im.List()).size,
    grow: 1,
    wrap: true,
    cell: (item) => {
      const actionsCount = item.getIn(["policy_json", "actions"], Im.List()).size;
      const isAdmin = item.get("is_admin");

      if (isAdmin) {
        return <span className="text-warning">All Permissions</span>;
      }

      return (
        <span className="text-muted">
          {actionsCount} {actionsCount === 1 ? "permission" : "permissions"}
        </span>
      );
    },
  },
  {
    name: "Actions",
    cell: (item) => (
      <Hide when={!props?.isAdmin}>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => props.setModal(<NewAuthGroupModal {...props} authGroup={item} />)}
          title="Edit Permission Group"
        >
          <Octicon name="pencil" />
        </button>
      </Hide>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    width: "100px",
  },
];

function ProjectPermissionsHeader({ projectId, permissionCount, isLoading }) {
  const { data: project } = useApi(`/api/project/{projectId}/`, { projectId });

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <h1 className="h3 mb-0 text-primary">Project Permissions</h1>
            </div>
            <p className="text-muted mb-2">{project?.get("name")}</p>
            {!isLoading && (
              <p className="text-muted small mb-0">
                <strong>Total permission groups:</strong> {permissionCount}
              </p>
            )}
          </div>
          <div className="text-end">
            <span className="badge badge-primary">Permission Management</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionsDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Permission Group Management</h2>
      <p className="text-muted">
        Configure permission groups that control what actions users can perform within the project. Each permission
        group defines specific access rights for different project functions.
      </p>
      <div className="alert alert-info">
        <div className="d-flex">
          <Octicon name="shield" className="me-2 mt-1" />
          <div>
            <strong>Permission Control:</strong> Permission groups can be assigned to roles, giving fine-grained control
            over user access to create, read, update, or delete different types of project data.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AuthGroupList({ projectId }) {
  let { setModal, hideModal } = useModal();
  let { isAdmin } = useProjectPermissions();
  let { data: authGroups, trigger, isLoading } = useApi("/api/project/{projectId}/authgroup/", { projectId });

  const permissionCount = authGroups?.size || 0;

  let _props = { projectId, setModal, hideModal, trigger, isAdmin };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <ProjectPermissionsHeader projectId={projectId} permissionCount={permissionCount} isLoading={isLoading} />

          <div className="card shadow-sm">
            <div className="card-body">
              <PermissionsDescription />

              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="mb-0">Permission Groups</h5>
                </div>
                <ProjectPermission projectId={projectId} requiresAdmin={true}>
                  <button
                    className="btn btn-primary d-flex align-items-center gap-2"
                    onClick={() => setModal(<NewAuthGroupModal {..._props} />)}
                  >
                    <Octicon name="plus" />
                    Add Permission Group
                  </button>
                </ProjectPermission>
              </div>

              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary me-3 mr-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading permissions...</span>
                </div>
              ) : (
                <div className="table-responsive">
                  <DataTable
                    columns={getColumns(_props)}
                    data={authGroups?.toArray() || []}
                    highlightOnHover={true}
                    pagination={permissionCount > 25}
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-5">
                        <Octicon name="shield" className="text-muted mb-3" style={{ fontSize: "48px" }} />
                        <h5 className="text-muted">No permission groups defined</h5>
                        <p className="text-muted">
                          This project doesn't have any permission groups yet. Create your first permission group to
                          control user access.
                        </p>
                        <ProjectPermission projectId={projectId} requiresAdmin={true}>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => setModal(<NewAuthGroupModal {..._props} />)}
                          >
                            <Octicon name="plus" className="me-2" />
                            Create First Permission Group
                          </button>
                        </ProjectPermission>
                      </div>
                    }
                    customStyles={{
                      table: {
                        style: {
                          borderRadius: "8px",
                        },
                      },
                      headRow: {
                        style: {
                          backgroundColor: "#f8f9fa",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        },
                      },
                      rows: {
                        style: {
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                          },
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const operations = ["CREATE", "READ", "UPDATE", "DELETE"];

const viewObjectTypes = {
  TRACKER_REF: "Work Packages",
  PROJECT_MEMBER: "Project Users",
  PROJECT_REPORT: "Project Dashboard",
  TRACKER_REF_SCORE_VALUE: "Project Reporting",
};

function ActionsTableRows({ actions, setActions }) {
  let rows = [];
  let counter = 0;
  for (let [objKey, name] of Object.entries(viewObjectTypes)) {
    let colCells = operations.map((operation, i) => {
      let action = `${operation}_${objKey}`;
      let isChecked = actions.has(action);
      return (
        <td key={counter++} className="text-center" style={{ backgroundColor: isChecked ? "#99EB99" : "#EB9999" }}>
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setActions(isChecked ? actions.delete(action) : actions.add(action))}
          />
        </td>
      );
    });
    // Add the row header
    colCells.unshift(
      <th key={counter++} scope="row">
        {name}
      </th>
    );
    // Add this to the rows
    rows.push(<tr key={counter++}>{colCells}</tr>);
  }
  return rows;
}

function ActionsTable(props) {
  let colHeadings = operations.map((o, i) => (
    <th key={i} scope="col" width="15%" className="text-center">
      {o}
    </th>
  ));
  colHeadings.unshift(<th key={colHeadings.length} scope="col" />);
  return (
    <div className="form-group row mb-0">
      <div className="col-sm-12">
        <table className="table table-sm">
          <thead>
            <tr>{colHeadings}</tr>
          </thead>
          <tbody>
            <ActionsTableRows {...props} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuthGroupForm(props) {
  let { authGroup, hideModal } = props;
  console.log("AuthGroupForm", authGroup?.toJS());
  let [name, setName] = useState(authGroup?.get("name") || "");
  let [isAdmin, setIsAdmin] = useState(authGroup?.get("is_admin") || false);
  let [actions, setActions] = useState(Im.Set(authGroup?.getIn(["policy_json", "actions"]) || Im.List()));

  return (
    <div className="container-fluid">
      <form>
        <div className="form-group mb-3">
          <label htmlFor="permissionName" className="form-label">
            Permission Group Name
          </label>
          <input
            type="text"
            name="name"
            id="permissionName"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for this permission group"
            required
          />
        </div>

        <div className="form-group mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="adminCheck"
              checked={isAdmin}
              onChange={() => setIsAdmin(!isAdmin)}
            />
            <label className="form-check-label" htmlFor="adminCheck">
              <strong>Administrator Access</strong>
              <br />
              <small className="text-muted">
                Grants full access to all project functions. When enabled, specific permissions below are ignored.
              </small>
            </label>
          </div>
        </div>

        <Hide when={isAdmin}>
          <div className="mb-3">
            <label className="form-label">Specific Permissions</label>
            <p className="text-muted small mb-3">
              Select the specific actions this permission group can perform. Green indicates allowed actions, red
              indicates restricted actions.
            </p>
            <ActionsTable actions={actions} setActions={setActions} />
          </div>
        </Hide>

        <div className="d-flex gap-2">
          <button
            className="btn btn-primary"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              updateAuthGroup({
                ...props,
                data: {
                  name,
                  is_admin: isAdmin,
                  policy_json: { actions },
                },
              });
            }}
          >
            <Octicon name="check" className="me-2" />
            {authGroup ? "Update Permission Group" : "Create Permission Group"}
          </button>
          <button className="btn btn-secondary" type="button" onClick={hideModal}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
